import Phaser from 'phaser';
import { Behavior } from '../behaviors/Behavior';
import { Allegiance } from '../../constants/enums';
import { DataManagerCollision, BoundingBox } from '../../systems/DataManagerCollision';
import { DataManagerImagePoint } from '../../systems/DataManagerImagePoint';
import { SpawnFlags } from '../../systems/DataManagerEntity';

export { Allegiance };

/**
 * Namespaced state for behavior-to-behavior communication.
 * Each behavior registers its state under its own namespace.
 * Existence of a namespace = behavior is present.
 */
export interface BehaviorStates {
  abductable?: { state: 'idle' | 'lifting' | 'released' };
  platform?: { isGrounded: boolean };
  flyer?: { isFlying: boolean };
  turret?: { isAiming: boolean; angle: number };
  solid?: Record<string, never>;  // Presence = solid, no properties needed
  walker?: {
    isStopped: boolean;
    isChasing: boolean;
    isPatrolling: boolean;
    isFleeing: boolean;
  };
  shooter?: {
    isShooting: boolean;
    isGuarding: boolean;
  };
}

export interface EntityConfig {
  hp?: number;
  maxHp?: number;
  allegiance?: Allegiance;
  behaviors?: Record<string, unknown>;
  spawnFlags?: SpawnFlags;
  // Animation initial state (from animations-overrides.json)
  initialAnimation?: string;
  initialFrame?: number;
  autoPlay?: boolean;
  [key: string]: unknown;
}

/**
 * Entity - Base class for all game entities
 *
 * Provides:
 * - Behavior system (add, get, update)
 * - Allegiance (player, enemy, neutral)
 * - Lifecycle management
 */
export class Entity extends Phaser.Physics.Arcade.Sprite {
  // ========== STATIC: String utilities (case-insensitive) ==========

  /**
   * Compara dos strings case-insensitive
   */
  static matches(a: string, b: string): boolean {
    return a.toLowerCase() === b.toLowerCase();
  }

  /**
   * Normaliza string a minúsculas. Emite warning si difiere del original.
   */
  static normalize(value: string): string {
    const normalized = value.toLowerCase();
    if (normalized !== value) {
      console.warn(`[CASE MISMATCH] Original: "${value}" → Normalizado: "${normalized}"`);
    }
    return normalized;
  }

  /**
   * Busca en un Map case-insensitive. Primero intenta exact match, luego lowercase.
   */
  static findInMap<T>(map: Map<string, T>, key: string): T | undefined {
    // Exact match first (fast path)
    const exact = map.get(key);
    if (exact !== undefined) return exact;

    // Case-insensitive fallback
    const lowerKey = key.toLowerCase();
    for (const [mapKey, value] of map) {
      if (mapKey.toLowerCase() === lowerKey) {
        return value;
      }
    }
    return undefined;
  }

  /**
   * Busca en un objeto/Record case-insensitive
   */
  static findInObject<T>(obj: Record<string, T>, key: string): T | undefined {
    // Exact match first
    if (obj[key] !== undefined) return obj[key];

    // Case-insensitive fallback
    const lowerKey = key.toLowerCase();
    for (const objKey of Object.keys(obj)) {
      if (objKey.toLowerCase() === lowerKey) {
        return obj[objKey];
      }
    }
    return undefined;
  }

  // ========== Instance properties ==========

  public config: EntityConfig;
  public behaviors: Behavior[] = [];
  public allegiance: Allegiance;

  /** Namespaced state for behavior-to-behavior communication */
  public state: { behaviors: BehaviorStates; [key: string]: unknown } = { behaviors: {} };
  public entityId?: string;
  public spawnData?: unknown; // Reference to spawn data for tracking

  /** SpawnFlags control culling/despawn behavior */
  public spawnFlags: Required<SpawnFlags> = {
    canDespawn: false,  // Default: NO destruir al salir de despawn zone
    canCull: true,      // Default: SI desactivar al salir de cull zone
    alwaysActive: false // Default: NO siempre activo
  };

  // Per-frame hitbox support
  protected collisionManager?: DataManagerCollision;
  protected imagePointManager?: DataManagerImagePoint;
  protected currentAnimName?: string;
  protected currentFrameIndex: number = 0;
  private animListenersActive = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    texture: string,
    config: EntityConfig = {}
  ) {
    // Use placeholder if texture doesn't exist or has no valid frames
    let safeTexture = texture;
    if (!scene.textures.exists(texture)) {
      console.warn(`[Entity] Missing texture "${texture}", using placeholder`);
      safeTexture = '__missing__';
    } else {
      // Check if texture has valid frame data (not just an empty atlas)
      const textureObj = scene.textures.get(texture);
      const frames = textureObj.getFrameNames();
      // If no frames or only __BASE frame, texture is likely broken
      if (frames.length === 0 || (frames.length === 1 && frames[0] === '__BASE')) {
        // Check if __BASE has valid dimensions
        const baseFrame = textureObj.get('__BASE');
        if (!baseFrame || baseFrame.width <= 1 || baseFrame.height <= 1) {
          console.warn(`[Entity] Texture "${texture}" has no valid frames, using placeholder`);
          safeTexture = '__missing__';
        }
      }
    }

    // Find initial frame from config, fallback to {texture}_default_0
    let initialFrame: string | undefined;
    if (safeTexture !== '__missing__') {
      const textureObj = scene.textures.get(safeTexture);
      const frames = textureObj.getFrameNames();

      // 1. First priority: use config.initialAnimation if specified
      const configAnim = config.initialAnimation;
      const configFrame = config.initialFrame ?? 0;
      if (configAnim) {
        const configFrameName = `${safeTexture}_${configAnim}_${configFrame}`;
        // Try exact match
        if (frames.includes(configFrameName)) {
          initialFrame = configFrameName;
        } else {
          // Case-insensitive search
          const lowerConfig = configFrameName.toLowerCase();
          for (const frame of frames) {
            if (frame.toLowerCase() === lowerConfig) {
              initialFrame = frame;
              break;
            }
          }
        }
      }

      // 2. Fallback: {texture}_default_0
      if (!initialFrame) {
        const defaultFrame = `${safeTexture}_default_0`;
        if (frames.includes(defaultFrame)) {
          initialFrame = defaultFrame;
        } else {
          // Case-insensitive search for default_0
          const lowerDefault = defaultFrame.toLowerCase();
          for (const frame of frames) {
            if (frame.toLowerCase() === lowerDefault) {
              initialFrame = frame;
              break;
            }
          }
        }
      }

      // 3. Last resort: first available frame (not __BASE)
      if (!initialFrame && frames.length > 0) {
        initialFrame = frames.find(f => f !== '__BASE') || frames[0];
      }
    }

    super(scene, x, y, safeTexture, initialFrame);

    // Add to scene
    scene.add.existing(this);
    scene.physics.add.existing(this);

    // If using placeholder, ensure it's visible
    if (safeTexture === '__missing__') {
      this.setVisible(true);
      this.setAlpha(1);
      this.setDepth(100); // High depth to ensure visibility
      this.setDisplaySize(32, 32); // Standard placeholder size
    }

    this.config = config;
    this.allegiance = config.allegiance || Allegiance.Neutral;

    // Apply spawn flags from config (merge with defaults)
    if (config.spawnFlags) {
      this.spawnFlags = { ...this.spawnFlags, ...config.spawnFlags };
    }

    // Get collision manager from registry for per-frame hitbox updates
    this.collisionManager = scene.registry.get('collisionManager') as DataManagerCollision | undefined;
    // Get image point manager from registry for spawn points
    this.imagePointManager = scene.registry.get('imagePointManager') as DataManagerImagePoint | undefined;

    // Apply initial animation/frame state from config
    this.applyInitialState();
  }

  /**
   * Apply initial animation/frame state from config (from animations-overrides.json)
   */
  protected applyInitialState(): void {
    const { initialAnimation, initialFrame = 0, autoPlay = false } = this.config;

    if (!initialAnimation) return;

    const texture = this.texture.key;
    if (texture === '__missing__') return;

    const frameName = `${texture}_${initialAnimation}_${initialFrame}`;
    const animKey = `${texture}_${initialAnimation}`;

    // Verify frame exists
    const textureObj = this.scene.textures.get(texture);
    const frames = textureObj.getFrameNames();

    if (!frames.includes(frameName)) {
      console.warn(`[Entity] Initial frame "${frameName}" not found for ${texture}`);
      return;
    }

    if (autoPlay && this.scene.anims.exists(animKey)) {
      this.play({ key: animKey, startFrame: initialFrame });
    } else {
      this.setFrame(frameName);
    }
  }

  /**
   * Add a behavior to this entity
   */
  addBehavior<T extends Behavior>(behavior: T): T {
    this.behaviors.push(behavior);
    return behavior;
  }

  /**
   * Get a behavior by its class type
   */
  getBehavior<T extends Behavior>(type: new (...args: unknown[]) => T): T | undefined {
    return this.behaviors.find(b => b instanceof type) as T | undefined;
  }

  /**
   * Check if entity has a specific behavior
   */
  hasBehavior<T extends Behavior>(type: new (...args: unknown[]) => T): boolean {
    return this.behaviors.some(b => b instanceof type);
  }

  /**
   * Phaser calls preUpdate automatically each frame for Sprites
   * We use this to update all behaviors
   */
  preUpdate(time: number, delta: number): void {
    // Call parent preUpdate (handles animations)
    super.preUpdate(time, delta);

    // Update all behaviors (enabled or with active processes)
    for (const behavior of this.behaviors) {
      if (behavior.enabled || this.hasActiveProcesses(behavior)) {
        behavior.update(time, delta);
      }
    }
  }

  /**
   * Update all behaviors (can be called manually if needed)
   */
  update(time: number, delta: number): void {
    for (const behavior of this.behaviors) {
      if (behavior.enabled || this.hasActiveProcesses(behavior)) {
        behavior.update(time, delta);
      }
    }
  }

  /**
   * Check if behavior has ongoing processes that need updating even when disabled
   * (e.g., abduction in progress)
   * NOTE: Falling is now handled by PlatformBehavior via entity.state.isGrounded
   */
  private hasActiveProcesses(behavior: Behavior): boolean {
    const abductable = behavior as unknown as { isBeingAbducted?: boolean };
    return abductable.isBeingAbducted === true;
  }

  /**
   * Enable/disable all behaviors
   */
  setAllBehaviorsEnabled(enabled: boolean): void {
    for (const behavior of this.behaviors) {
      behavior.enabled = enabled;
    }
  }

  /**
   * Reset entity state for reuse (pooling support)
   * Override in subclasses for specific reset logic
   */
  reset(config: Record<string, unknown> = {}): void {
    // Merge with existing config
    this.config = { ...this.config, ...config };
    this.allegiance = (config.allegiance as Allegiance) || this.allegiance;

    // Reset velocity if physics body exists
    if (this.body) {
      this.body.velocity.set(0, 0);
    }

    // Re-enable all behaviors
    this.setAllBehaviorsEnabled(true);
  }

  /**
   * Called when entity enters the camera viewport
   * Override for custom visibility behavior
   */
  onEnterView(): void {
    // Enable animation listeners for per-frame hitbox
    if (!this.animListenersActive) {
      this.on('animationstart', this.onAnimationStart, this);
      this.on('animationupdate', this.onAnimationUpdate, this);
      this.animListenersActive = true;
    }
  }

  /**
   * Called when entity exits the camera viewport
   * Override for custom visibility behavior
   */
  onExitView(): void {
    // Disable animation listeners when not visible
    if (this.animListenersActive) {
      this.off('animationstart', this.onAnimationStart, this);
      this.off('animationupdate', this.onAnimationUpdate, this);
      this.animListenersActive = false;
    }
  }

  /**
   * Handle animation start - extract animation name for hitbox lookup
   */
  protected onAnimationStart(anim: Phaser.Animations.Animation): void {
    // Extract animation name: "laser_Guarda" → "Guarda"
    this.currentAnimName = anim.key.includes('_')
      ? anim.key.split('_').slice(1).join('_')
      : anim.key;
  }

  /**
   * Handle animation frame change - update hitbox per-frame
   */
  protected onAnimationUpdate(
    _anim: Phaser.Animations.Animation,
    frame: Phaser.Animations.AnimationFrame
  ): void {
    // Track current frame index for image point lookups
    this.currentFrameIndex = frame.index;

    if (!this.collisionManager || !this.currentAnimName) return;

    const hitbox = this.collisionManager.getFrameHitbox(
      this.texture.key,
      this.currentAnimName,
      frame.index
    );

    if (hitbox) {
      this.updatePhysicsBodyFromHitbox(hitbox);
    }
  }

  /**
   * Get world position of a named image point.
   * Image points are defined per sprite/animation/frame in C3.
   *
   * @param pointName - Name of the image point (e.g., 'WeaponCenter', 'TractorBeam')
   * @returns World position {x, y} or undefined if point not found
   */
  getImagePoint(pointName: string): { x: number; y: number } | undefined {
    if (!this.imagePointManager) return undefined;

    // Use current animation name, fallback to 'default'
    const animName = this.currentAnimName || 'default';

    // Use frame dimensions (scaled) instead of displayWidth/Height
    // This ensures imagepoints follow the actual frame size during animations
    // where frame sizes change (like Farm1's "transform" animation)
    const frameWidth = this.frame.width * this.scaleX;
    const frameHeight = this.frame.height * this.scaleY;

    return this.imagePointManager.getWorldPosition(
      this.texture.key,      // sprite name (e.g., 'player')
      animName,              // current animation
      this.currentFrameIndex, // current frame
      pointName,             // point name (e.g., 'WeaponCenter')
      this.x,                // world X
      this.y,                // world Y
      frameWidth,            // frame width (scaled)
      frameHeight,           // frame height (scaled)
      this.originX,          // origin X (ignored by getWorldPosition)
      this.originY,          // origin Y (ignored by getWorldPosition)
      this.rotation          // sprite rotation in radians
    );
  }

  /**
   * Update physics body size and offset from hitbox data
   */
  protected updatePhysicsBodyFromHitbox(hitbox: BoundingBox): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    // Use FRAME size (unscaled) - Phaser Arcade internally applies sprite scale
    const frame = this.frame;

    const bodyWidth = frame.width * hitbox.widthRatio;
    const bodyHeight = frame.height * hitbox.heightRatio;
    const offsetX = frame.width * hitbox.offsetXRatio;
    const offsetY = frame.height * hitbox.offsetYRatio;

    body.setSize(bodyWidth, bodyHeight);
    body.setOffset(offsetX, offsetY);
  }

  /**
   * Clean up behaviors on destroy
   */
  destroy(fromScene?: boolean): void {
    for (const behavior of this.behaviors) {
      behavior.destroy();
    }
    this.behaviors = [];
    super.destroy(fromScene);
  }
}
